import type { CSSProperties } from "react";
import { BRANCH_PLACEMENTS, TREND_YEARS, branchTotal } from "../../lib/placementTrends";

/**
 * FIG-PLC-004 — total students placed per batch, and which branch made up
 * that total, 2022 to 2026.
 *
 * One bar per year. Bar HEIGHT is that year's total, scaled against the
 * tallest year, so heights compare across years at a glance. Bar COMPOSITION
 * is each branch's share of that year, via flex-grow ratios on the segments —
 * exactly proportional, no separate pixel math per segment.
 *
 * Fourteen branches is far past the point where distinct hues stay
 * distinguishable, so colour does not try to identify a branch on its own.
 * Branches are ranked by their five-year total and bucketed two-per-step into
 * the site's seven-step navy ramp, biggest ranks deepest — the stack reads as
 * a grounded gradient, not a single undifferentiated blob, and colour groups
 * branches by size tier rather than claiming to name each one. Position (the
 * same rank order in every bar), the hover tooltip, and the legend carry
 * actual identity.
 *
 * A branch absent from a year (the four newest programmes, before 2026)
 * simply contributes no segment that year — not a zero-height sliver.
 */
const MAX_BAR_PX = 280;

const RANKED = [...BRANCH_PLACEMENTS]
  .map((b) => ({ ...b, allTime: b.byYear.reduce((n: number, v) => n + (v ?? 0), 0) }))
  .sort((a, b) => b.allTime - a.allTime);

/** Two ranks share each of the seven steps; rank 0 (biggest) gets the deepest. */
const stepOf = (rank: number) => 6 - Math.min(6, Math.floor(rank / 2));

const YEAR_TOTALS = TREND_YEARS.map((y) => branchTotal(y));
const MAX_YEAR_TOTAL = Math.max(...YEAR_TOTALS);

export function PlacementBranchGrid() {
  return (
    <figure className="pfig">
      <div className="pfig-head">
        <span className="pfig-meta">
          FIG-PLC-004 &middot; students placed per batch, by branch &middot; source: BIET
          Training &amp; Placement Cell
        </span>
      </div>

      <div className="pfig-vbars">
        {TREND_YEARS.map((y, yi) => {
          const yearTotal = YEAR_TOTALS[yi];
          const stackPx = (yearTotal / MAX_YEAR_TOTAL) * MAX_BAR_PX;
          const segs = RANKED.map((b, rank) => ({
            code: b.code,
            label: b.label,
            n: b.byYear[yi],
            step: stepOf(rank),
          })).filter((s): s is typeof s & { n: number } => s.n !== null && s.n > 0);

          return (
            <div className="pfig-vbar-col" key={y}>
              <span className="pfig-vbar-total">{yearTotal}</span>
              <div className="pfig-vbar-stack" style={{ height: `${stackPx}px` }}>
                {segs.map((s) => {
                  const segPx = (s.n / yearTotal) * stackPx;
                  return (
                    <div
                      key={s.code}
                      className="pfig-vbar-seg"
                      style={
                        {
                          flex: `${s.n} ${s.n} 0`,
                          "--c": `var(--chart-seq-${s.step + 1})`,
                          "--ink": `var(--chart-ink-${s.step + 1})`,
                        } as CSSProperties
                      }
                      data-tip={`${s.label} · ${y} · ${s.n} of ${yearTotal} placed`}
                      tabIndex={0}
                    >
                      {segPx >= 16 ? s.n : ""}
                    </div>
                  );
                })}
              </div>
              <span className="pfig-year">{y}</span>
            </div>
          );
        })}
      </div>

      <div className="pfig-legend">
        {RANKED.map((b, rank) => (
          <span key={b.code} data-tip={b.label} tabIndex={0}>
            <i
              className="pfig-key"
              style={{ "--k": `var(--chart-seq-${stepOf(rank) + 1})` } as CSSProperties}
            />
            {b.code}
          </span>
        ))}
      </div>
    </figure>
  );
}
